<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('role.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('role.pageTitle'),
            href: '/roles'
          },
          {
            text: $t('create'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <ts-loading-banner :loading="loading">
          <form class="form-horizontal">
            <fieldset>
              <legend>{{ $t('role.roleUser') }}</legend>
              <div class="row form-group">
                <label class="col-md-3 col-form-label required">{{
                  $t('role.roleName')
                }}</label>
                <div class="col-md-3">
                  <input
                    v-model.trim="model.name"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.has('name') }"
                    autofocus
                  />
                  <div class="invalid-feedback" v-if="errors.has('name')">
                    {{ errors.first('name') }}
                  </div>
                </div>
              </div>
              <div class="row form-group">
                <label class="col-md-3 col-form-label required">{{
                  $t('role.guardName')
                }}</label>
                <div class="col-md-3">
                  <select
                    class="form-control"
                    :class="{ 'is-invalid': errors.has('guard_name') }"
                    v-model="model.guard_name"
                    @change="filterPermission"
                  >
                    <option value="staff-api">{{
                      $t('role.officeStaff')
                    }}</option>
                    <option value="teacher-api">{{
                      $t('role.teacher')
                    }}</option>
                    <option value="parent-api">{{ $t('role.parent') }}</option>
                    <option value="student-api">{{
                      $t('role.student')
                    }}</option>
                  </select>
                  <div class="invalid-feedback" v-if="errors.has('name')">
                    {{ errors.first('guard_name') }}
                  </div>
                </div>
              </div>
            </fieldset>
            <div class="row">
              <div class="col-md-12">
                <div id="container">
                  <table class="table table-hover ui-table ts-table">
                    <thead>
                      <tr>
                        <th class="ts-sticky ts-top-0 ts-z-10">
                          <input
                            v-model="searchPermissionText"
                            type="text"
                            style="width:300px"
                            class="form-control"
                            :placeholder="$t('search')"
                          />
                        </th>
                        <th
                          v-for="column in permissionColumns"
                          :key="column"
                          class="ts-sticky ts-top-0 ts-z-10 ts-w-20 ts-text-center"
                        >
                          <div
                            class="ts-flex ts-flex-col ts-justify-center ts-items-center ts-w-full"
                          >
                            <p>{{ column }}</p>
                            <ts-checkbox
                              :checkedValue="allCheckedBy(column)"
                              @change="checked => selectAllBy(column, checked)"
                            />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="(group, groupName) in permissionGroups">
                        <tr>
                          <td>
                            <span
                              class="ts-text-sm ts-font-semibold ts-pt-6 ts-inline-block"
                              >{{ groupName }}</span
                            >
                          </td>
                          <td v-for="n in permissionColumns.length" :key="n">
                            <div class="ts-flex ts-justify-center ts-pt-6">
                              <ts-checkbox
                                :checkedValue="
                                  allCheckedByGroup(
                                    groupName,
                                    permissionColumns[n - 1]
                                  )
                                "
                                @change="
                                  checked =>
                                    selectByGroup(
                                      groupName,
                                      permissionColumns[n - 1],
                                      checked
                                    )
                                "
                              />
                            </div>
                          </td>
                        </tr>
                        <tr
                          v-for="(permissions, resourceName) in group"
                          :key="resourceName"
                        >
                          <td>
                            <span class="ts-ml-4 ts-mr-6">{{
                              resourceName
                            }}</span>
                          </td>
                          <td v-for="n in permissionColumns.length" :key="n">
                            <!-- <span>{{ isShow(permissions, n) }}</span> -->
                            <div class="ts-flex ts-justify-center">
                              <ts-checkbox
                                v-model="model.permissions"
                                :value="isShow(permissions, n).id"
                                v-if="isShow(permissions, n) !== undefined"
                              />
                            </div>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
                <div class="validate" v-if="errors.has('permissions')">
                  {{ errors.first('permissions') }}
                </div>
              </div>
            </div>
            <br />
            <div class="row">
              <div class="col-md-12 text-right">
                <ts-button
                  class="mr-2"
                  @click.prevent="$router.push({ name: 'role' })"
                  >{{ $t('cancel') }}</ts-button
                >
                <ts-button
                  outline
                  color="primary"
                  class="mr-2"
                  :waiting="waiting_new"
                  @click.prevent="saveAddNew"
                  >{{ $t('save_add_new') }}</ts-button
                >
                <ts-button
                  color="primary"
                  :waiting="waiting"
                  @click.prevent="save"
                  >{{ $t('save') }}</ts-button
                >
              </div>
            </div>
          </form>
        </ts-loading-banner>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { Errors } from 'form-backend-validation'
import {
  cloneDeep,
  union,
  uniq,
  difference,
  intersection,
  flatMap,
  map,
  filter
} from 'lodash'

export default {
  name: 'roleCreate',
  data() {
    return {
      model: {
        guard_name: 'staff-api',
        name: '',
        permissions: []
      },
      errors: new Errors(),
      loading: false,
      waiting: false,
      waiting_new: false
    }
  },
  created() {
    this.fetchPermissions()
  },
  computed: {
    ...mapState('role', ['permissions']),
    ...mapGetters('role', ['permissionGroups', 'permissionColumns']),
    searchPermissionText: {
      get() {
        return this.$store.state.role.searchPermissionText
      },
      set(newValue) {
        this.$store.commit('role/SET_SEARCH_PERMISSION', newValue)
      }
    },
    idPermissionsBy: vm => column => {
      return uniq(
        map(
          filter(vm.permissions, p => {
            return p.name.toLowerCase().startsWith(column.toLowerCase())
          }),
          'id'
        )
      )
    },
    allCheckedBy: vm => column => {
      const idPermissions = vm.idPermissionsBy(column)

      return (
        intersection(vm.model.permissions, idPermissions).length ===
        idPermissions.length
      )
    },
    idPermissionsGroup: vm => (groupName, column) => {
      return uniq(
        map(
          filter(flatMap(vm.permissionGroups[groupName]), p => {
            return p.name.toLowerCase().startsWith(column.toLowerCase())
          }),
          'id'
        )
      )
    },
    allCheckedByGroup: vm => (groupName, column) => {
      const idPermissions = vm.idPermissionsGroup(groupName, column)

      return (
        intersection(vm.model.permissions, idPermissions).length ===
        idPermissions.length
      )
    },
    isShow: vm => (permission, pOrder) => {
      switch (pOrder) {
        case 1:
          return permission.filter(
            p => p.name.toLowerCase().indexOf('view') >= 0
          )[0]
        case 2:
          return permission.filter(
            p => p.name.toLowerCase().indexOf('create') >= 0
          )[0]

        case 3:
          return permission.filter(
            p => p.name.toLowerCase().indexOf('update') >= 0
          )[0]

        case 4:
          return permission.filter(
            p => p.name.toLowerCase().indexOf('delete') >= 0
          )[0]

        default:
          return undefined
      }
    }
  },
  methods: {
    ...mapActions('role', ['store']),
    fetchPermissions(attributes) {
      this.loading = true

      this.$store
        .dispatch('role/fetchPermissions', attributes)
        .finally(() => (this.loading = false))
    },
    selectAllBy(column, checked) {
      if (checked) {
        this.model.permissions = union(
          this.model.permissions,
          this.idPermissionsBy(column)
        )
      } else {
        this.model.permissions = difference(
          this.model.permissions,
          this.idPermissionsBy(column)
        )
      }
    },
    selectByGroup(groupName, column, checked) {
      if (checked) {
        this.model.permissions = union(
          this.model.permissions,
          this.idPermissionsGroup(groupName, column)
        )
      } else {
        this.model.permissions = difference(
          this.model.permissions,
          this.idPermissionsGroup(groupName, column)
        )
      }
    },
    filterPermission() {
      this.model.name = ''
      this.model.permissions = []
      this.fetchPermissions({
        guard_name: this.model.guard_name
      })
    },
    save() {
      this.errors = new Errors()
      this.waiting = true
      this.store(this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.clearInput()
          this.$router.push({ name: 'role' })
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    saveAddNew() {
      this.errors = new Errors()
      this.waiting_new = true
      this.store(this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.clearInput()
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting_new = false
        })
    },
    clearInput() {
      ;(this.model.name = ''), (this.model.permissions = [])
    }
  }
}
</script>

<style lang="css" scoped>
#container {
  min-height: 40vh;
  max-height: 60vh;
  overflow-y: scroll;
}

.table > thead > tr > th,
.table > tbody > tr > th,
.table > tfoot > tr > th,
.table > thead > tr > td,
.table > tbody > tr > td,
.table > tfoot > tr > td {
  border-color: #e2e7eb;
  padding: 4px 2px !important;
}
</style>
